// /staff/card/funnel: where people stop in the card request (pr/card/dropoff.py). For a date range: how many reached
// each screen, how many stopped there, the question and error they were on when they stopped, and the median time on
// the screen. Staff trying the form are left out. No answers are kept, so none show.
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib'
import { Busy, Err, Fresh, Page, Section, Sources, Table, num, pct } from '../staff/kit'
import { when } from '../staff/common'

type Count = { n: number }
type ScreenRow = { screen: string; reached: number; left: number; had_error: number; median_seconds: number | null
  last_fields: ({ field: string } & Count)[]; last_errors: ({ error: string } & Count)[] }
type Stopped = { who: string; user_id: string | null; application_id: string | null; email?: string | null; source?: string | null
  last_screen: string; last_field: string | null; last_error: string | null; last_at: string; visits: number }
type Funnel = { from: string; to: string; people: number; visits: number; sent: number; screens: ScreenRow[]; stopped: Stopped[]; capped: boolean }

const NAME: Record<string, string> = { welcome: 'Welcome', you: 'You', company: 'Company', business: 'Business', card: 'Card', people: 'People' }
const name = (s: string) => NAME[s] ?? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ')
const day = (d: Date) => d.toISOString().slice(0, 10)
const top = (xs: { n: number }[], label: (x: never) => string) => xs.map((x) => `${label(x as never)} (${x.n})`).join(', ')
const secs = (s: number | null) => (s === null ? '–' : s < 90 ? `${Math.round(s)}s` : `${Math.round(s / 6) / 10}m`)

export function StaffCardFunnel() {
  const [params, setParams] = useSearchParams()
  const to = params.get('to') ?? day(new Date())
  const from = params.get('from') ?? day(new Date(Date.now() - 29 * 86_400_000))
  const [draft, setDraft] = useState({ from, to })
  const q = useQuery({ queryKey: ['staff', 'card', 'funnel', from, to],
    queryFn: () => api<Funnel>('GET', `/api/staff/card/funnel?from=${from}&to=${to}`) })
  const d = q.data
  return (
    <Page title="Where people stop" testid="staff-card-funnel"
      sub={<Sources>
        <Fresh at={q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null} label="Form events" live />
        <span className="st-fresh">One person per sign-in, else per visit. Staff left out</span>
      </Sources>}
      right={<Link className="st-btn" to="/staff/card">Card requests</Link>}>
      <form className="st-filters" onSubmit={(e) => { e.preventDefault(); setParams(draft, { replace: true }) }}>
        <label className="st-field">From<input className="st-input" type="date" data-testid="staff-card-funnel-from" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} /></label>
        <label className="st-field">To<input className="st-input" type="date" data-testid="staff-card-funnel-to" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} /></label>
        <button type="submit" className="st-btn">Show</button>
      </form>
      {q.isLoading ? <Busy what="Loading the funnel" /> : q.error ? <Err error={q.error} /> : d ? (
        <>
          <p className="st-note" data-testid="staff-card-funnel-totals">
            {num(d.people)} people, {num(d.visits)} visits, {num(d.sent)} sent ({pct(d.sent, d.people)}).
            {d.capped ? ' Too many events in this range: pick a shorter one.' : ''}
          </p>
          <Section title="Screens" count={d.screens.length}>
            <Table rows={d.screens} rowKey={(r) => r.screen} testid="staff-card-funnel-table"
              rowAttrs={(r) => ({ 'data-testid': 'staff-card-funnel-row', 'data-screen': r.screen, 'data-reached': String(r.reached), 'data-left': String(r.left) })}
              columns={[
                { key: 'screen', head: 'Screen', primary: true, cell: (r) => name(r.screen) },
                { key: 'reached', head: 'Reached', num: true, cell: (r) => num(r.reached) },
                { key: 'of', head: 'Of all', num: true, cell: (r) => pct(r.reached, d.people) },
                { key: 'left', head: 'Stopped here', num: true, cell: (r) => num(r.left) },
                { key: 'err', head: 'Saw an error', num: true, cell: (r) => num(r.had_error) },
                { key: 'time', head: 'Median time', num: true, cell: (r) => secs(r.median_seconds) },
                { key: 'field', head: 'Last question before stopping', cell: (r) => top(r.last_fields, (x: { field: string }) => x.field) },
                { key: 'error', head: 'Last error before stopping', cell: (r) => top(r.last_errors, (x: { error: string }) => x.error) },
              ]} />
          </Section>
          <Section title="Stopped, newest first" count={d.stopped.length}>
            <Table rows={d.stopped} rowKey={(r) => r.who} testid="staff-card-funnel-stopped"
              rowAttrs={(r) => ({ 'data-testid': 'staff-card-funnel-person', 'data-screen': r.last_screen, 'data-email': r.email ?? '' })}
              columns={[
                { key: 'who', head: 'Who', primary: true, cell: (r) => r.user_id
                  ? <Link className="st-link" to={`/staff/record/users/${r.user_id}`}>{r.email ?? r.user_id}</Link> : 'Signed out' },
                { key: 'screen', head: 'Stopped on', cell: (r) => name(r.last_screen) },
                { key: 'field', head: 'Question', cell: (r) => r.last_field },
                { key: 'error', head: 'Error', cell: (r) => r.last_error },
                { key: 'source', head: 'Came from', cell: (r) => r.source },
                { key: 'req', head: 'Request', cell: (r) => (r.application_id ? <Link className="st-link" to={`/staff/card/${r.application_id}`}>Open</Link> : null) },
                { key: 'visits', head: 'Visits', num: true, cell: (r) => num(r.visits) },
                { key: 'at', head: 'Last seen', nowrap: true, cell: (r) => when(r.last_at) },
              ]} />
          </Section>
        </>
      ) : null}
    </Page>
  )
}
